import * as moment from 'moment';
import * as React from 'react';
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { signOut as signOutAction } from "../../actions/loginActions";
import { clearMailData as clearMailDataAction, refreshList as refreshListAction, savePaginationInfo as savePaginationInfoAction, selectMessage as selectMessageAction } from "../../actions/mailActions";
import { addNotification as addNotificationAction, clearNotifications as clearNotificationsAction } from "../../actions/notificationsActions";
import { MailService } from '../../services/mail';
import Notification from '../common/Notification';
import Message from "./model/Message";
import Paging from "./model/Paging";

interface IPaginatorProps {
  currentPage: number;
  csrf: string;
  loginTime: number;
  addNotification: (notification: Notification) => any;
  clearMailData: () => any;
  clearNotifications: () => any;
  refreshList: (messages: Message[]) => any;
  selectMessage: (messages: Message) => any;
  savePaginationInfo: (paginationInfo: Paging) => any;
  signOut: () => any;
  user: string;
  wsid: string;
  sortField: string;
  isDesc: boolean;
  paginationInfo: Paging;
}

interface IPaginatorState {
  currentPage: number;
}

export class Paginator extends React.Component<IPaginatorProps, IPaginatorState> {
  public mailService: MailService;

  constructor(props: IPaginatorProps) {
    super(props);
    this.state = { currentPage: this.props.currentPage };
    this.mailService = new MailService(this.props.user, this.props.wsid, this.props.csrf);
    this.goToPage = this.goToPage.bind(this);
  }

  public getNumberOfPages(): number {
    const paginationInfo = this.props.paginationInfo;
    if (!paginationInfo || !paginationInfo.total || !paginationInfo.pageSize) {
      return 0;
    }
    return Math.ceil(this.props.paginationInfo.total / this.props.paginationInfo.pageSize);
  }

  public async goToPage(page: number) {
    const numberOfPages = this.getNumberOfPages();
    if (page >= 0 && page < numberOfPages && page !== this.props.paginationInfo.page) {
      try {
        const response = await this.mailService.list(page, this.props.paginationInfo.pageSize, this.props.isDesc ? 'desc' : 'asc', this.props.sortField);

        if (!response.ok) {
          throw new Error(response.status.toString());
        }

        const msgList = await response.json();
        this.props.refreshList(msgList.messages);
        this.props.savePaginationInfo(msgList.paging); // save pagination info
        this.props.selectMessage(msgList.messages[0]); // select first message by default
      } catch (e) {
        if (e.message === '401') {
          if (moment().diff(this.props.loginTime, 'hours') < 12) {
            this.props.addNotification({ message: "Session Timeout", isError: true });
          }
          this.props.signOut();
          this.props.clearMailData();
        }
        this.props.addNotification({ messageKey: "L.Error.Retrieve", isError: true });
      }
    }
  }

  public renderListElement(page: number, selected: boolean) {
    return (
      <li className="page-item" key={page}>
        <a className={"page-link " + (selected ? "selected" : '')} onClick={this.goToPage.bind(this, page - 1)}>{page}</a>
      </li>
    );
  }

  public renderHellip(location: string) {
    return (
      <li className="page-item" key={`${location}hellip`}>
        <span className="page-link disabled">&hellip;</span>
      </li>
    );
  }

  public render() {
    const numberOfPages = this.getNumberOfPages();

    const pageNumbers = [];
    if (numberOfPages < 10) {
      pageNumbers.push(this.renderListElement(1, this.props.paginationInfo.page === 0 || numberOfPages === 0));
      for (let i = 2; i <= numberOfPages; i++) {
        pageNumbers.push(this.renderListElement(i, this.props.paginationInfo.page === i - 1));
      }
    } else {
      const activePage = this.props.paginationInfo.page + 1;
      if (activePage <= 5) {
        // Display the first 7, ellipsis, and final
        for (let i = 1; i <= 7; i++) {
          pageNumbers.push(this.renderListElement(i, activePage === i));
        }
        pageNumbers.push(this.renderHellip('post'));
        pageNumbers.push(this.renderListElement(numberOfPages, false));
      } else if (activePage > numberOfPages - 5) {
        // Display the first, ellipsis, and final 7
        pageNumbers.push(this.renderListElement(1, false));
        pageNumbers.push(this.renderHellip('pre'));
        for (let i = numberOfPages - 6; i <= numberOfPages; i++) {
          pageNumbers.push(this.renderListElement(i, activePage === i));
        }
      } else {
        // Display the first, ellipsis, 5 centered at the active page, ellipsis, and final
        pageNumbers.push(this.renderListElement(1, false));
        pageNumbers.push(this.renderHellip('pre'));
        for (let i = activePage - 2; i <= activePage + 2; i++) {
          pageNumbers.push(this.renderListElement(i, activePage === i));
        }
        pageNumbers.push(this.renderHellip('post'));
        pageNumbers.push(this.renderListElement(numberOfPages, false));
      }
    }

    return (
      <div className="row justify-content-end align-items-center list-sep" >
        <ul className="pagination">
          <li className="page-item">
            <a className="page-link" aria-label="Previous" onClick={this.goToPage.bind(this, this.props.paginationInfo.page - 1)}>
              <span aria-hidden="true">&laquo;</span>
              <span className="sr-only">Previous</span>
            </a>
          </li>
          {pageNumbers}
          <li className="page-item">
            <a className="page-link" aria-label="Next" onClick={this.goToPage.bind(this, this.props.paginationInfo.page + 1)}>
              <span aria-hidden="true">&raquo;</span>
              <span className="sr-only">Next</span>
            </a>
          </li>
        </ul>
      </div>
    );
  }
}

const mapStateToProps = (state: any) => {
  return {
    csrf: state.authentication.csrf,
    isDesc: state.mail.isDesc,
    loginTime: state.authentication.loginTime,
    messageList: state.mail.messageList,
    paginationInfo: state.mail.paginationInfo,
    sortField: state.mail.sortField,
    user: state.authentication.user,
    wsid: state.authentication.wsid,
  };
}

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    addNotification: bindActionCreators(addNotificationAction, dispatch),
    clearMailData: bindActionCreators(clearMailDataAction, dispatch),
    clearNotifications: bindActionCreators(clearNotificationsAction, dispatch),
    refreshList: bindActionCreators(refreshListAction, dispatch),
    savePaginationInfo: bindActionCreators(savePaginationInfoAction, dispatch),
    selectMessage: bindActionCreators(selectMessageAction, dispatch),
    signOut: bindActionCreators(signOutAction, dispatch)
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Paginator);



// WEBPACK FOOTER //
// ./src/components/mail/Paginator.tsx